import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PersonalComposer } from "../../src/personal-composer-contract.ts";
import { personalComposerClient } from "./personal-composer-client.ts";
import type { ComposerWords } from "./personal-composer-content.ts";
import { type ComposerSessionState, PersonalComposerSession } from "./personal-composer-session.ts";
import { acknowledgeUnsent, readUnsent } from "./unsent-reply.ts";

export const PERSONAL_COMPOSER_POLL_MS = 5_000;
const SAVE_DELAY_MS = 400;

export function usePersonalComposer(input: {
  workspaceId: string;
  planId: string;
  unsentKey: string;
  sourceMessageId: string | null;
  held: boolean;
  hydrate(composer: PersonalComposer): void;
  snapshot(): ComposerWords;
}) {
  const latest = useRef(input);
  latest.current = input;
  const alive = useRef(true);
  const currentSession = useRef<PersonalComposerSession | null>(null);
  const [state, setState] = useState<ComposerSessionState>({
    mode: "loading",
    saving: false,
    dirty: false,
    conflict: null,
    conflictKind: null,
    error: "",
  });
  const session = useMemo(() => {
    const created = new PersonalComposerSession(
      personalComposerClient(input.workspaceId, input.planId),
      readUnsent(input.unsentKey),
      {
        changed: (next) => {
          if (alive.current && currentSession.current === created) setState(next);
        },
        hydrate: (composer) => {
          if (alive.current && currentSession.current === created) latest.current.hydrate(composer);
        },
        acknowledge: (words, revision, version) =>
          acknowledgeUnsent(input.unsentKey, words, revision, version),
      },
      latest.current.sourceMessageId,
    );
    return created;
  }, [input.workspaceId, input.planId, input.unsentKey]);
  currentSession.current = session;
  useEffect(() => {
    alive.current = true;
    void session.start().then(() => {
      if (alive.current) setState(session.state);
    });
    const refresh = () => {
      if (!latest.current.held && document.visibilityState !== "hidden") void session.refresh();
    };
    const timer = setInterval(refresh, PERSONAL_COMPOSER_POLL_MS);
    window.addEventListener("focus", refresh);
    return () => {
      alive.current = false;
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [session]);
  useEffect(() => {
    if (!state.dirty || state.mode !== "enabled" || state.conflict || input.held) return;
    const timer = setTimeout(() => {
      void session.flush().catch(() => {});
    }, SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [session, state.dirty, state.mode, state.conflict, input.held]);
  const confirmed = useCallback(
    async (id: string) => session.confirmed(id, latest.current.snapshot()),
    [session],
  );
  return {
    state,
    blocked: state.mode === "loading" || state.mode === "error" || state.conflict !== null,
    edit: (words: ComposerWords) => session.edit(words),
    prepare: (id: string, words: ComposerWords) => session.prepare(id, words),
    confirmed,
    retry: () => {
      void session.recover().catch(() => {});
    },
    keepLocal: () => {
      void session.keepLocal().catch(() => {});
    },
    chooseSaved: () => session.chooseSaved(),
  };
}

export type PersonalComposerControl = ReturnType<typeof usePersonalComposer>;

export function PersonalComposerNotice({ control }: { control: PersonalComposerControl }) {
  const { state } = control;
  if (state.conflict) {
    const saved = state.conflict.composer.content;
    return (
      <div className="lcr-reply-notice" data-tone="warn" role="alert">
        <p>
          {state.conflictKind === "stale_thread"
            ? "The conversation changed. Review it before keeping this draft. Your words are still here."
            : "A different draft was saved. Your words are still in the reply box."}
        </p>
        <details style={{ flexBasis: "100%" }}>
          <summary>Review saved draft</summary>
          <p
            style={{
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              maxHeight: "12rem",
              overflowY: "auto",
            }}
          >
            {saved?.plainText || "The saved reply box is empty."}
          </p>
        </details>
        <button type="button" className="lcr-small-button lcr-ghost" onClick={control.chooseSaved}>
          Use saved draft
        </button>
        <button type="button" className="lcr-small-button lcr-ghost" onClick={control.keepLocal}>
          Keep my draft
        </button>
      </div>
    );
  }
  if (state.error)
    return (
      <div className="lcr-reply-notice" data-tone="warn" role="alert">
        <p>{state.error}</p>
        <button type="button" className="lcr-small-button lcr-ghost" onClick={control.retry}>
          Retry saving draft
        </button>
      </div>
    );
  if (state.mode === "loading")
    return (
      <div className="lcr-reply-notice" role="status">
        <p>Loading your saved draft…</p>
      </div>
    );
  return null;
}
